import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/client'
import { ROUTES } from '../routes'
import Button from './Button'
import Input from './Input'
import SearchableSelect from './SearchableSelect'
import Select from './Select'
import { InlineAlert } from './ui'
import plusIcon from '../assets/figma/plus.svg'

// Общие части редактора сценария и выгрузки прошлых периодов (history-export,
// решение владельца 2026-10-13): фильтр, сопоставление полей, тумблер
// «Отслеживать» и справочники значений — одни компоненты и одна логика.

export const CONNECTOR_NAMES = { amocrm: 'amoCRM', bitrix24: 'Bitrix24' }

export const FINTABLO_FIELD_LABELS = {
  name: 'Название сделки',
  // «Полной суммы» (`amountFull`) здесь нет: Финтабло её не сохраняет, цель
  // убрана из деклараций 2026-09-14, а сохранённые правила сервер отдаёт уже
  // как «Сумму».
  amount: 'Сумма',
  currency: 'Валюта',
  customCostPrice: 'Себестоимость',
  statusId: 'Статус',
  responsibleId: 'Ответственный',
  partnerId: 'Контрагент',
  directionId: 'Направление',
  startDate: 'Дата начала',
  endDate: 'Дата окончания',
  comment: 'Комментарий',
  actDate: 'Дата акта',
  nds: 'НДС'
}

export const SOURCE_FIELD_LABELS = {
  name: 'Название сделки',
  price: 'Бюджет сделки',
  // См. декларацию amoCRM: воронка выбирается вместе со стадией.
  status_id: 'Воронка и стадия',
  responsible_user_id: 'Ответственный',
  created_at: 'Дата создания',
  closed_at: 'Дата закрытия',
  TITLE: 'Название сделки',
  // Поля Bitrix24 называются так, как их называет Bitrix24, — иначе пользователь
  // их не найдёт (решение владельца 2026-09-14). С подписью «Бюджет сделки»,
  // словом amoCRM, пользователь искал сумму и не находил вовсе. Названия сверены
  // по карточке сделки и списку «Выбор полей» портала 2026-09-14. Оговорка:
  // валюта в Финтабло пока не передаётся, хотя в названии суммы она есть — см.
  // BACKLOG.
  OPPORTUNITY: 'Сумма и валюта',
  STAGE_ID: 'Стадия',
  ASSIGNED_BY_ID: 'Ответственный',
  COMPANY_ID: 'Компания',
  CONTACT_ID: 'Контакт',
  // Прежний источник цели «Контрагент» — строка-пояснение вместо ключа поля.
  // Подпись оставлена для сопоставлений, сохранённых до её замены.
  'COMPANY_ID (fallback CONTACT_ID)': 'Компания или контакт',
  DATE_CREATE: 'Дата создания',
  CLOSEDATE: 'Дата завершения',
  автогенерация: 'Автогенерация'
}

// Сохранённые до 2026-10-03 «содержит» / «не содержит» по компании и контакту
// Bitrix24 искали подстроку id записи из справочника. Открываем их как
// «равно» / «не равно» — так их сравнивает и сервер; при сохранении сценарий
// запишет новый оператор (bitrix24-counterparty-filter-operators).
export const LEGACY_COUNTERPARTY_OPERATORS = { contains: 'eq', not_contains: 'neq' }

export function exactCounterpartyCondition (condition, fields) {
  const field = fields.find(item => item.key === condition?.field)
  const operator = LEGACY_COUNTERPARTY_OPERATORS[condition?.operator]
  return COUNTERPARTY_SOURCES[field?.options_source] && operator ? { ...condition, operator } : condition
}

// Тумблер «Отслеживать» (ADR R19). Тексты — решения владельца 2026-10-09 и
// 2026-10-12. «Комментарий» генерирует Платформа — тумблера у него нет.
export const TRACKING_HINT = 'При изменении этого поля в источнике данные в Финтабло будут перезаписываться, даже если менялись вручную.'
export const TRACKABLE_SYSTEM_TARGETS = new Set(['name'])
// Подсказка как в Финтабло: карточка над «?» при наведении. На телефоне
// наведения нет — там карточку открывает и закрывает касание.
export function TrackingToggle ({ checked, disabled, hint, id, label, onChange }) {
  const [hintOpen, setHintOpen] = useState(false)
  const hintId = `scenario-tracking-hint-${id}`
  return (
    <div className='scenario-editor__tracking'>
      <button
        aria-checked={checked}
        aria-label={`Отслеживать: ${label}`}
        className='scenario-editor__switch'
        disabled={disabled}
        onClick={() => onChange(!checked)}
        role='switch'
        type='button'
      />
      <span aria-hidden='true'>Отслеживать</span>
      {hint && (
        <span className='scenario-editor__tracking-help-wrap'>
          <button
            aria-describedby={hintOpen ? hintId : undefined}
            aria-label='Что значит «Отслеживать»'
            className='scenario-editor__tracking-help'
            data-open={hintOpen || undefined}
            onClick={() => setHintOpen(open => !open)}
            onKeyDown={event => { if (event.key === 'Escape') setHintOpen(false) }}
            onMouseEnter={() => setHintOpen(true)}
            onMouseLeave={() => setHintOpen(false)}
            type='button'
          >
            ?
          </button>
          {hintOpen && <span className='scenario-editor__tracking-tooltip' id={hintId} role='tooltip'>{TRACKING_HINT}</span>}
        </span>
      )}
    </div>
  )
}

export function normalizeFintabloStatuses (response) {
  return (response?.statuses || []).map(status => ({
    value: String(status.value ?? status.id ?? status.statusId ?? status.status_id ?? ''),
    label: status.label || status.name || status.statusName || status.status_name || String(status.id || '')
  })).filter(status => status.value)
}

export function hasPipelineStages (response) {
  return (response?.pipelines || []).some(pipeline => (pipeline.statuses || []).length > 0)
}

export function fieldError (validation, field) {
  return validation?.errors?.find(error => error.field === field)
}

export function withSelectedOption (options, selected) {
  if (!selected || options.some(option => option.value === selected)) return options
  return [{ value: selected, label: `${selected} — значение недоступно` }, ...options]
}

// Стадия amoCRM приходит парой «воронка:стадия»: у системных стадий один
// status_id во всех воронках («Успешно реализовано» — всегда 142). Сценарий,
// сохранённый до 2026-09-25, хранит голый status_id и срабатывает на эту
// стадию в любой воронке. Такое значение называется прямо, а не «значение
// недоступно»: оно работает, но пользователь должен видеть, что воронки в нём нет.
export function withBareStageOption (slug, options, selected) {
  if (slug !== 'amocrm' || !selected || String(selected).includes(':')) return options
  if (options.some(option => option.value === selected)) return options
  const stage = options.find(option => String(option.value).endsWith(`:${selected}`))
  if (!stage) return options
  return [{ value: selected, label: `${stage.label} — без воронки, сработает в любой воронке` }, ...options]
}

export function optionLabel (option) {
  const group = option.groupLabel || option.group_label
  return group ? `${group} — ${option.label}` : option.label
}

// Воронка входит в подпись самой стадии, а не остаётся заголовком над группой.
// Заголовок пользователь пропускает: он уезжает за пределы видимой части
// списка, а в свёрнутом поле после выбора остаётся одно «Сделка успешна» —
// стадия чужой воронки выглядит ровно так же, как нужная. Так у первого
// внешнего пользователя сценарий молча не сработал: фильтр ждал `C4:WON` из
// «Аренды контейнеров», а сделки шли по «Продажам/логистике». Строка длиннее,
// зато воронка видна и при выборе, и после него.
export function dropdownOptions (options = []) {
  return options.map(option => ({
    ...option,
    value: String(option.value ?? ''),
    label: optionLabel(option),
    group_label: null,
    groupLabel: null
  }))
}

export function ScenarioDropdown ({
  onChange,
  options = [],
  placeholder = 'Выберите',
  searchable = false,
  ...props
}) {
  const normalized = dropdownOptions(options)
  if (searchable) {
    return (
      <SearchableSelect
        {...props}
        onChange={value => onChange({ target: { value } })}
        options={normalized}
        placeholder={placeholder}
      />
    )
  }
  return (
    <Select {...props} onChange={onChange}>
      <option value=''>{placeholder}</option>
      {normalized.map(option => (
        <option disabled={option.disabled} key={option.value} value={option.value}>
          {optionLabel(option)}
        </option>
      ))}
    </Select>
  )
}

export function uniqueOptions (items) {
  return [...new Map(items.filter(item => item.value).map(item => [item.value, item])).values()]
}

// Какие цели заняты системными сопоставлениями — правило одного источника:
// список действий, при которых сопоставление выполняется, объявлен в декларации
// коннектора (`appliesTo`) и приезжает сюда вместе с ней. Редактор не держит
// собственного списка действий — иначе он снова разойдётся с сервером, как это
// было с «Название сделки» в сценарии изменения.
export function systemMappingAppliesTo (mapping, actionType) {
  if (actionType === null || actionType === undefined) return true
  if (!Array.isArray(mapping.appliesTo)) return true
  return mapping.appliesTo.includes(actionType)
}

export function mappingOptions (scenarioFields, liveFields = [], actionType = null) {
  const declaration = scenarioFields?.scenario1
  const fields = declaration?.fields || []
  const systemMappings = (declaration?.systemMappings || [])
    .filter(mapping => systemMappingAppliesTo(mapping, actionType))
  const systemTargets = new Set([
    ...systemMappings.map(mapping => mapping.targetField),
    'statusId'
  ])
  const editableFields = fields.filter(field => {
    if (systemTargets.has(field.targetField)) return false
    if (field.sourcePattern) {
      const pattern = new RegExp(field.sourcePattern)
      return liveFields.some(source => pattern.test(source.key))
    }
    return liveFields.some(source => source.key === field.source)
  })
  return {
    fields,
    fintablo: uniqueOptions(editableFields.map(field => ({
      value: field.targetField,
      label: FINTABLO_FIELD_LABELS[field.targetField] || field.targetField
    }))),
    // Для стандартных полей сделки подпись берём свою: портал отдаёт их
    // заголовки на английском («Company ID»), и в интерфейсе это читается как
    // машинный ключ. Пользовательские поля сохраняют название из CRM —
    // там название придумал сам пользователь.
    // Поле с `mappable: false` — только для условия фильтра.
    source: liveFields.filter(field => field.mappable !== false).map(field => ({
      value: field.key,
      label: SOURCE_FIELD_LABELS[field.key] || field.label || field.key
    })),
    systemMappings
  }
}

export function matchesMappingDeclaration (field, source, target, sourceType) {
  const sourceMatches = field.source === source ||
    (field.sourcePattern && new RegExp(field.sourcePattern).test(source || ''))
  const typeMatches = !field.sourceTypes?.length || field.sourceTypes.includes(sourceType)
  return sourceMatches && field.targetField === target && typeMatches
}

export function mappingLogic (fields, source, target, sourceType) {
  const declaration = fields.find(field => matchesMappingDeclaration(field, source, target, sourceType))
  if (declaration) return declaration.transform || 'передаётся без преобразования'
  if (source && target) return 'передаётся без преобразования'
  return 'логика появится после выбора'
}

// Справочник, где делается сопоставление цели, объявляет поле коннектора
// (`reference`), а не редактор по имени цели. Слово «справочника» в тексте
// логики становится ссылкой на него в новой вкладке: сценарий с
// несохранёнными правками остаётся открытым (решение владельца 2026-10-02).
export const REFERENCE_WORD = 'справочника'

export function MappingLogic ({ fields, slug, source, target, sourceType }) {
  const text = mappingLogic(fields, source, target, sourceType)
  const reference = fields.find(field => matchesMappingDeclaration(field, source, target, sourceType))?.reference
  const at = reference ? text.lastIndexOf(REFERENCE_WORD) : -1
  if (at < 0) return <span>{text}</span>
  return (
    <span>
      {text.slice(0, at)}
      <Link rel='noopener noreferrer' target='_blank' to={ROUTES.REFERENCE(slug, reference)}>{REFERENCE_WORD}</Link>
      {text.slice(at + REFERENCE_WORD.length)}
    </span>
  )
}

export function validFilter (filter) {
  if (filter?.mode !== 'filter') return true
  return filter.groups?.length > 0 && filter.groups.every(group =>
    group.length > 0 && group.every(condition =>
      condition.field &&
      condition.operator &&
      (
        NO_VALUE_OPERATORS.includes(condition.operator) ||
        (
          String(condition.value ?? '').trim() &&
          (condition.field_type !== 'user' || /^\d+$/.test(String(condition.value))) &&
          // У диапазона обязательны обе границы: с одной сценарий сохранялся бы
          // и не срабатывал.
          (!RANGE_OPERATORS.includes(condition.operator) || String(condition.value_to ?? '').trim())
        )
      )
    )
  )
}

// Условия фильтра приезжают вместе с полем в live-fields: набор и подписи
// заданы один раз на сервере, в runtime-модуле фильтров. Своей таблицы здесь
// быть не должно — прежняя разошлась с серверной, и для поля-даты предлагались
// строковые «содержит»/«не содержит», которых сервер не принимает.
export function filterOperators (field, condition) {
  if (Array.isArray(field?.operators)) return field.operators
  // Поля нет среди живых — сценарий сохранён на поле, которого больше нет в
  // CRM. Показываем сохранённое условие как есть, сервер отдельно сообщит, что
  // поле не поддерживается.
  return condition?.operator ? [{ value: condition.operator, label: condition.operator }] : []
}

export const RANGE_OPERATORS = ['between']
export const NO_VALUE_OPERATORS = ['empty', 'not_empty']

// Поля сделки Bitrix24, значение которых — сущность справочника контрагентов.
// Тип поля здесь не помогает: портал отдаёт их как строку.
// Откуда берутся значения условия, решает объявление поля, а не его имя.
// Раньше здесь был список полей, которым «положен» список: стадия, ответственный,
// компания, контакт. Всё остальное проваливалось в текстовый ввод, и каждое новое
// поле с конечным набором приходилось чинить отдельной задачей.
//
// Два вида объявления:
//   options        — набор приехал вместе с полем (перечисление провайдера,
//                    воронки, типы сделки). Ничего дополнительно грузить не нужно.
//   options_source — значения лежат в справочнике, который редактор уже загрузил;
//                    дублировать их в каждом поле было бы лишним трафиком.
export const DIRECTORY_PLACEHOLDERS = {
  statuses: 'Выберите воронку и стадию',
  employees: 'Выберите сотрудника',
  counterparties_company: 'Выберите компанию',
  counterparties_contact: 'Выберите контакт'
}

// Источник значений условия → тип записи справочника контрагентов.
export const COUNTERPARTY_SOURCES = {
  counterparties_company: 'company',
  counterparties_contact: 'contact'
}
export const COUNTERPARTY_SEARCH_DELAY_MS = 300

// Справочник контрагентов подключения для условия фильтра. Строки Bitrix24
// приходят с полями b24_*, строки amoCRM — в форме хаба (source_*).
export const COUNTERPARTY_DIRECTORIES = {
  bitrix24: {
    list: (connectionId, params) => api.getBitrix24CounterpartyMappings(connectionId, params),
    row: item => ({ type: item.b24_entity_type, id: item.b24_entity_id, name: item.b24_name })
  },
  amocrm: {
    list: (connectionId, params) => api.getAmocrmCounterpartyMappings(connectionId, params),
    row: item => ({ type: item.source_type, id: item.source_id, name: item.source_name })
  }
}

// Варианты значения из страницы справочника. Устаревшие строки не предлагаем:
// этих сущностей в CRM уже нет, и фильтр по ним никогда не сработает.
export function counterpartyOptionsOf (page, type, directory) {
  return (page?.mappings || [])
    .filter(item => item.is_stale !== true)
    .map(directory.row)
    .filter(row => row.type === type)
    .map(row => ({
      value: String(row.id),
      label: row.name || String(row.id)
    }))
}

// Сохранённые значения условий по компании и контакту — им нужна подпись.
export function savedCounterpartyValues (filters, fields) {
  const seen = new Set()
  const result = []
  for (const condition of (filters?.groups || []).flat()) {
    const type = COUNTERPARTY_SOURCES[fields.find(field => field.key === condition.field)?.options_source]
    const value = condition.value === undefined || condition.value === null ? '' : String(condition.value)
    if (!type || !value || seen.has(`${type}:${value}`)) continue
    seen.add(`${type}:${value}`)
    result.push({ type, value })
  }
  return result
}

export function filterValueChoices (field, directories) {
  if (Array.isArray(field?.options) && field.options.length > 0) {
    return { options: field.options, placeholder: 'Выберите значение' }
  }
  const source = field?.options_source
  if (!source) return null
  const options = directories[source]
  // Источник объявлен, а справочник не загрузился: подставлять свободный ввод
  // нельзя — он вернёт ровно тот дефект, ради которого список и заводился.
  return { options: options || [], placeholder: DIRECTORY_PLACEHOLDERS[source] || 'Выберите значение' }
}

// Сделка не может быть одновременно в одной воронке и на стадии другой:
// такой фильтр сохраняется, выглядит настроенным и не срабатывает никогда.
// Сопоставляем по идентификатору воронки, а не по названию: названия
// повторяются и меняются.
export function pipelineConflict (group, statuses, pipelineField, stageField) {
  const pipeline = group.find(condition => condition.field === pipelineField && condition.operator === 'eq')
  const stage = group.find(condition => condition.field === stageField && condition.operator === 'eq')
  if (!pipeline?.value || !stage?.value) return null
  const stageOption = statuses.find(option => String(option.value) === String(stage.value))
  if (!stageOption || stageOption.group_id === undefined || stageOption.group_id === null) return null
  if (String(stageOption.group_id) === String(pipeline.value)) return null
  return stageOption.group_label || null
}

export function comparisonRuleHint (operator) {
  if (['eq', 'neq'].includes(operator)) return 'Сравнение точное: значение должно совпадать полностью.'
  if (['contains', 'not_contains'].includes(operator)) return 'Сравнение по вхождению текста, регистр не важен.'
  if (['includes', 'not_includes'].includes(operator)) return 'Значение сравнивается целиком с одним из выбранных.'
  return null
}

export function issuesFor (validation, fieldPrefix, type = 'errors') {
  return (validation?.[type] || []).filter(issue =>
    issue.field === fieldPrefix || issue.field.startsWith(`${fieldPrefix}.`)
  )
}

// Шаг «Фильтр»: режим и условия по текущим данным сделки. `activeStep` и
// `triggerStepValid` — доступность шага в редакторе (у выгрузки первым шагом
// идёт период).
export function ScenarioFilterBody ({
  activeStep,
  addAndCondition,
  addOrCondition,
  counterpartyChoices,
  crmName,
  filterFields,
  form,
  isReadOnly,
  liveFields,
  loadProviderOptions,
  providerOptions,
  removeFilterCondition,
  searchCounterparties,
  setFilterMode,
  slug,
  sourceEmployees,
  statuses,
  triggerStepValid,
  updateFilterCondition
}) {
  return (
    <>
      <p className='scenario-editor__step-description'>Ограничить сделки по текущим данным:</p>
      <div className='scenario-editor__segmented' role='group' aria-label='Режим фильтра'>
        <Button
          aria-pressed={form.filters.mode === 'none'}
          disabled={activeStep < 2 || !triggerStepValid || isReadOnly}
          onClick={() => setFilterMode('none')}
          variant={form.filters.mode === 'none' ? 'solid' : 'stroke'}
        >
          Без фильтров
        </Button>
        <Button
          aria-pressed={form.filters.mode === 'filter'}
          disabled={activeStep < 2 || !triggerStepValid || isReadOnly}
          onClick={() => setFilterMode('filter')}
          variant={form.filters.mode === 'filter' ? 'solid' : 'stroke'}
        >
          С фильтром
        </Button>
      </div>
      {form.filters.mode === 'filter' && (
        <div className='scenario-editor__filter-groups'>
          {form.filters.groups.map((group, groupIndex) => {
            // Сделка не может быть и в воронке A, и на стадии воронки C:
            // такая пара условий сохраняется и не срабатывает никогда.
            const conflictPipeline = pipelineConflict(
              group,
              statuses,
              slug === 'amocrm' ? 'pipeline_id' : 'CATEGORY_ID',
              slug === 'amocrm' ? 'status_id' : 'STAGE_ID'
            )
            return (
              <div className='scenario-editor__filter-group' key={`filter-group-${groupIndex}`}>
                {groupIndex > 0 && <div className='scenario-editor__filter-join scenario-editor__filter-join--or'>ИЛИ</div>}
                {conflictPipeline && (
                  <InlineAlert title='Условия противоречат друг другу' tone='warning'>
                    Выбранная стадия относится к воронке «{conflictPipeline}» — это стадия из другой воронки,
                    чем указана в условии. Сделка не может удовлетворять обоим условиям сразу, и сценарий
                    не сработает никогда.
                  </InlineAlert>
                )}
                {group.map((condition, conditionIndex) => {
                  const field = liveFields.find(item => item.key === condition.field)
                  const operators = filterOperators(field, condition)
                  const withoutValue = NO_VALUE_OPERATORS.includes(condition.operator)
                  const isDateField = (field?.type || condition.field_type) === 'date'
                  const isRange = RANGE_OPERATORS.includes(condition.operator)
                  // Справочник портала грузится по выбору поля, поэтому у него
                  // есть собственные состояния: пока грузится — поле недоступно,
                  // при отказе — названа причина и есть повтор. Свободный ввод
                  // не подставляется ни в одном из них.
                  const providerState = field?.options_source === 'provider'
                    ? providerOptions[field.key] || { status: 'loading', options: [] }
                    : null
                  const choices = providerState
                    ? {
                        options: providerState.options,
                        placeholder: providerState.status === 'loading' ? 'Загружаем значения…' : 'Выберите значение'
                      }
                    : filterValueChoices(field, {
                      statuses,
                      employees: sourceEmployees,
                      counterparties_company: counterpartyChoices('company', condition.value),
                      counterparties_contact: counterpartyChoices('contact', condition.value)
                    })
                  const counterpartyType = COUNTERPARTY_SOURCES[field?.options_source] || null
                  const fromDirectory = Boolean(choices)
                  // Где значение набирается руками, правило сравнения нужно назвать:
                  // сравнение буквальное, и опечатка даёт сценарий, который выглядит
                  // настроенным и никогда не срабатывает. Пока поле не выбрано,
                  // подсказывать нечего — строка ещё пустая.
                  const comparisonHint = !condition.field || fromDirectory || isDateField || withoutValue
                    ? null
                    : comparisonRuleHint(condition.operator)
                  return (
                    <React.Fragment key={`filter-${groupIndex}-${conditionIndex}`}>
                      {conditionIndex > 0 && <div className='scenario-editor__filter-join scenario-editor__filter-join--and'>И</div>}
                      <div className='scenario-editor__filter-row'>
                        <label className='scenario-editor__field'>
                          <span>Поле {crmName}</span>
                          <ScenarioDropdown
                            aria-label={`Поле ${crmName} ${conditionIndex + 1}`}
                            disabled={activeStep < 2 || !triggerStepValid || isReadOnly}
                            options={filterFields.map(item => ({
                              value: item.key,
                              label: SOURCE_FIELD_LABELS[item.key] || item.label || item.key
                            }))}
                            placeholder='Выберите поле'
                            searchable
                            value={condition.field}
                            onChange={event => {
                              const selected = liveFields.find(item => item.key === event.target.value)
                              updateFilterCondition(groupIndex, conditionIndex, {
                                field: event.target.value,
                                field_type: selected?.type || 'string',
                                // Условие по умолчанию берём из набора самого поля: «равно»
                                // для даты или множественного выбора не существует.
                                operator: selected?.operators?.[0]?.value || 'eq',
                                value: '',
                                value_to: ''
                              })
                            }}
                          />
                        </label>
                        <label className='scenario-editor__field'>
                          <span>Условие</span>
                          <ScenarioDropdown
                            aria-label={`Условие ${conditionIndex + 1}`}
                            disabled={activeStep < 2 || !condition.field || isReadOnly}
                            options={operators}
                            placeholder='Выберите условие'
                            searchable
                            value={condition.operator}
                            onChange={event => updateFilterCondition(groupIndex, conditionIndex, {
                              operator: event.target.value,
                              // Вторая граница остаётся только у диапазона: иначе она
                              // молча уехала бы в сохранённое условие другого вида.
                              value_to: RANGE_OPERATORS.includes(event.target.value) ? condition.value_to || '' : ''
                            })}
                          />
                        </label>
                        <label className='scenario-editor__field'>
                          <span>Значение</span>
                          {choices
                            ? (
                              <ScenarioDropdown
                                aria-label={`Значение ${conditionIndex + 1}`}
                                disabled={activeStep < 2 || withoutValue || isReadOnly ||
                                  (providerState ? providerState.status !== 'ready' : false)}
                                // Сохранённое значение остаётся в списке, даже если
                                // его больше нет в справочнике: затирать чужую
                                // настройку молча нельзя.
                                options={withSelectedOption(
                                  field?.options_source === 'statuses'
                                    ? withBareStageOption(slug, choices.options, condition.value)
                                    : choices.options,
                                  condition.value
                                )}
                                placeholder={choices.placeholder}
                                searchable
                                value={condition.value}
                                onQueryChange={counterpartyType && COUNTERPARTY_DIRECTORIES[slug]
                                  ? text => searchCounterparties(counterpartyType, text)
                                  : undefined}
                                onChange={event => updateFilterCondition(groupIndex, conditionIndex, { value: event.target.value })}
                              />
                              )
                            : (
                              <Input
                                aria-label={`Значение ${conditionIndex + 1}`}
                                disabled={activeStep < 2 || withoutValue || isReadOnly}
                                // Дата выбирается календарём: свободный ввод давал
                                // формат, который сравнивался как текст.
                                type={isDateField ? 'date' : 'text'}
                                value={condition.value}
                                onChange={event => updateFilterCondition(groupIndex, conditionIndex, { value: event.target.value })}
                              />
                              )}
                          {providerState?.status === 'error' && (
                            <small className='scenario-editor__field-error'>
                              Не удалось загрузить значения из CRM.{' '}
                              <button
                                className='ui-button ui-button--link'
                                onClick={() => loadProviderOptions(field.key, { force: true })}
                                type='button'
                              >
                                Повторить загрузку значений
                              </button>
                            </small>
                          )}
                          {comparisonHint && <small>{comparisonHint}</small>}
                        </label>
                        {isRange && (
                          <label className='scenario-editor__field'>
                            <span>Вторая дата</span>
                            <Input
                              aria-label={`Вторая дата ${conditionIndex + 1}`}
                              disabled={activeStep < 2 || isReadOnly}
                              type={isDateField ? 'date' : 'text'}
                              value={condition.value_to || ''}
                              onChange={event => updateFilterCondition(groupIndex, conditionIndex, { value_to: event.target.value })}
                            />
                          </label>
                        )}
                        {!isReadOnly && (
                          <Button
                            aria-label={`Удалить условие ${conditionIndex + 1}`}
                            disabled={activeStep < 2}
                            onClick={() => removeFilterCondition(groupIndex, conditionIndex)}
                            variant='ghost'
                          >
                            Удалить
                          </Button>
                        )}
                      </div>
                    </React.Fragment>
                  )
                })}
              </div>
            )
          })}
          {!isReadOnly && (
            <div className='scenario-editor__filter-actions'>
              <Button disabled={activeStep < 2} onClick={addAndCondition} variant='ghost'>+И</Button>
              <Button disabled={activeStep < 2} onClick={addOrCondition} variant='ghost'>+ИЛИ</Button>
            </div>
          )}
        </div>
      )}
    </>
  )
}

// Шаг «Маппинг полей»: системные строки, сопоставления с тумблером
// «Отслеживать», проверка и добавление строки.
export function ScenarioMappingBody ({
  actionStepValid,
  activeStep,
  addFieldMapping,
  crmName,
  fieldMappingRules,
  fieldOptions,
  form,
  isReadOnly,
  liveFields,
  removeRule,
  ruleErrors,
  ruleWarnings,
  slug,
  systemRows,
  toggleCounterpartyCreation,
  toggleRuleTracking,
  toggleSystemTracking,
  trackingAvailable,
  // Карточка завершённой выгрузки: строки только для чтения, тумблеры
  // «Отслеживать» работают (history-export, решение владельца 2026-10-13).
  trackingEditable = false,
  updateRule,
  validation
}) {
  const trackingDisabled = isReadOnly && !trackingEditable
  return (
    <>
      <div className='scenario-editor__mapping-head' aria-hidden='true'>
        <span>Поле {crmName}</span><span>Поле Финтабло</span><span>Логика и статус</span><span />
      </div>
      <div className='scenario-editor__rules scenario-editor__field-mappings'>
        {activeStep >= 4 && systemRows.map(mapping => (
          <div className='scenario-editor__mapping-row scenario-editor__mapping-row--system' key={`${mapping.source}-${mapping.targetField}`}>
            <div className='scenario-editor__mapping-readonly'>
              <span className='scenario-editor__mapping-label'>Поле {crmName}</span>
              <Input
                aria-label={`Системное поле ${crmName}: ${mapping.sourceLabel || SOURCE_FIELD_LABELS[mapping.source] || mapping.source}`}
                className='scenario-editor__mapping-readonly-value'
                value={mapping.sourceLabel || SOURCE_FIELD_LABELS[mapping.source] || mapping.source}
                readOnly
                disabled
              />
              <small>{mapping.source}</small>
            </div>
            <div className='scenario-editor__mapping-readonly'>
              <span className='scenario-editor__mapping-label'>Поле Финтабло</span>
              <Input
                aria-label={`Системное поле Финтабло: ${mapping.targetLabel || FINTABLO_FIELD_LABELS[mapping.targetField] || mapping.targetField}`}
                className='scenario-editor__mapping-readonly-value'
                value={mapping.targetLabel || FINTABLO_FIELD_LABELS[mapping.targetField] || mapping.targetField}
                readOnly
                disabled
              />
              <small>{mapping.targetField}</small>
            </div>
            <div className='scenario-editor__rule-logic'>
              <span>{mapping.transform || 'передаётся без преобразования'}</span>
              <small data-tone='success'>
                {mapping.required ? '✓ Обязательное поле' : '✓ Передаётся автоматически'}
              </small>
              {trackingAvailable && TRACKABLE_SYSTEM_TARGETS.has(mapping.targetField) && (
                <TrackingToggle
                  checked={(form.action.tracked_system_targets || []).includes(mapping.targetField)}
                  disabled={trackingDisabled}
                  hint
                  id={`system-${mapping.targetField}`}
                  label={mapping.targetLabel || FINTABLO_FIELD_LABELS[mapping.targetField] || mapping.targetField}
                  onChange={next => toggleSystemTracking(mapping.targetField, next)}
                />
              )}
            </div>
            <span aria-hidden='true' />
          </div>
        ))}
        {fieldMappingRules.map(({ rule, index }, displayIndex) => {
          const sourceIssues = issuesFor(validation, `rules.${index}.config.source`)
          const targetIssues = issuesFor(validation, `rules.${index}.config.target`)
          const directRuleIssues = (validation.errors || []).filter(issue => issue.field === `rules.${index}`)
          const ruleIssues = [...sourceIssues, ...targetIssues, ...directRuleIssues]
          const sourceValue = rule.config?.source || ''
          const targetValue = rule.config?.target || ''
          const isBlank = !sourceValue && !targetValue
          const isPartial = Boolean(sourceValue) !== Boolean(targetValue)
          const selectedLiveField = liveFields.find(field => field.key === sourceValue)
          const compatibleSources = targetValue
            ? fieldOptions.source.filter(option => {
              const liveField = liveFields.find(field => field.key === option.value)
              return fieldOptions.fields.some(field =>
                matchesMappingDeclaration(field, option.value, targetValue, liveField?.type)
              )
            })
            : fieldOptions.source
          const sourceSelectOptions = withSelectedOption(compatibleSources, sourceValue)
          const targetSelectOptions = withSelectedOption(fieldOptions.fintablo, targetValue)
          return (
            <div className='scenario-editor__mapping-row' key={`field_mapping-${index}`}>
              <label className='scenario-editor__mapping-field'>
                <span className='scenario-editor__mapping-label'>Поле {crmName}</span>
                <ScenarioDropdown
                  aria-label={`Поле ${crmName} ${displayIndex + 1}`}
                  disabled={isReadOnly || activeStep < 4 || !actionStepValid}
                  error={sourceIssues.length > 0}
                  options={sourceSelectOptions}
                  placeholder='Выберите'
                  searchable
                  value={sourceValue}
                  onChange={event => updateRule(index, current => {
                    const selected = liveFields.find(field => field.key === event.target.value)
                    return {
                      ...current,
                      config: {
                        ...current.config,
                        source: event.target.value,
                        source_type: selected?.type || null
                      }
                    }
                  })}
                />
                {sourceIssues.map(issue => <small className='scenario-editor__field-error' key={`${issue.code}-${issue.field}`}>{issue.message}</small>)}
              </label>
              <div className='scenario-editor__mapping-field'>
                <span className='scenario-editor__mapping-label'>Поле Финтабло</span>
                <ScenarioDropdown
                  aria-label={`Поле Финтабло ${displayIndex + 1}`}
                  disabled={isReadOnly || activeStep < 4 || !actionStepValid}
                  error={targetIssues.length > 0}
                  options={targetSelectOptions}
                  placeholder='Выберите'
                  searchable
                  value={targetValue}
                  onChange={event => updateRule(index, current => {
                    const nextTarget = event.target.value
                    const currentSource = current.config?.source || ''
                    const currentField = liveFields.find(field => field.key === currentSource)
                    const sourceAllowed = !nextTarget || fieldOptions.fields.some(field =>
                      matchesMappingDeclaration(field, currentSource, nextTarget, currentField?.type)
                    )
                    return {
                      ...current,
                      config: {
                        ...current.config,
                        source: sourceAllowed ? currentSource : '',
                        source_type: sourceAllowed ? current.config?.source_type || null : null,
                        target: nextTarget
                      }
                    }
                  })}
                />
                {targetValue && <small>{targetValue}</small>}
                {targetIssues.map(issue => <small className='scenario-editor__field-error' key={`${issue.code}-${issue.field}`}>{issue.message}</small>)}
                {targetValue === 'partnerId' && toggleCounterpartyCreation && ['amocrm', 'bitrix24'].includes(slug) && (
                  <div className='scenario-editor__tracking scenario-editor__counterparty-creation'>
                    <button
                      aria-checked={form.action.create_partners === true}
                      aria-label='Создавать новых контрагентов в Финтабло'
                      className='scenario-editor__switch'
                      disabled={isReadOnly || activeStep < 4 || !actionStepValid}
                      onClick={() => toggleCounterpartyCreation(form.action.create_partners !== true)}
                      role='switch'
                      type='button'
                    />
                    <span>Создавать новых контрагентов в Финтабло</span>
                  </div>
                )}
              </div>
              <div className='scenario-editor__rule-logic'>
                <MappingLogic
                  fields={fieldOptions.fields}
                  slug={slug}
                  source={sourceValue}
                  sourceType={selectedLiveField?.type}
                  target={targetValue}
                />
                <small data-tone={ruleIssues.length || isPartial ? 'error' : isBlank ? 'neutral' : 'success'}>
                  {ruleIssues.length || isPartial
                    ? '⊗ Заполните оба поля или удалите строку'
                    : isBlank
                      ? 'Пустая строка не будет сохранена'
                      : '✓ Сопоставлено'}
                </small>
                {directRuleIssues.map(issue => <small className='scenario-editor__field-error' key={`${issue.code}-${issue.field}`}>{issue.message}</small>)}
                {trackingAvailable && (
                  <TrackingToggle
                    checked={rule.config?.tracked === true}
                    disabled={trackingDisabled}
                    hint
                    id={`rule-${index}`}
                    label={FINTABLO_FIELD_LABELS[targetValue] || targetValue || `строка ${displayIndex + 1}`}
                    onChange={next => toggleRuleTracking(index, next)}
                  />
                )}
              </div>
              {!isReadOnly && <Button variant='ghost' onClick={() => removeRule(index)} aria-label={`Удалить правило ${displayIndex + 1}`}>Удалить</Button>}
            </div>
          )
        })}
      </div>
      {activeStep >= 4 && fieldMappingRules.length === 0 && (
        <p className='scenario-editor__empty'>
          {form.action?.type === 'update_deal'
            ? 'Добавьте хотя бы одно поле для изменения'
            : fieldOptions.systemMappings.length
              ? `Без дополнительных полей будут переданы название сделки, ID ${crmName} в комментарии и выбранная стадия.`
              : 'Дополнительных правил нет. Сценарий использует базовую логику шаблона.'}
        </p>
      )}
      {ruleErrors.map(issue => <p className='scenario-editor__mapping-issue' data-tone='error' key={`${issue.code}-${issue.field}`}>{issue.message}</p>)}
      {ruleWarnings.map(issue => <p className='scenario-editor__mapping-issue' data-tone='warning' key={`${issue.code}-${issue.field}`}>{issue.message}</p>)}
      {!isReadOnly && activeStep >= 4 && actionStepValid && (
        <div className='scenario-editor__add-field'>
          <Button aria-label='Добавить поле' variant='ghost' onClick={addFieldMapping}><img alt='' src={plusIcon} /> Добавить поле</Button>
        </div>
      )}
    </>
  )
}

// Стадии CRM для условия «Стадия». Bitrix24 обновляет кэш воронок; если портал
// не ответил, показываются сохранённые — об этом надо сказать, иначе
// пользователь выберет стадию не той воронки. amoCRM без стадий не работает.
export async function syncCrmStages (slug, userId) {
  if (!userId) return null
  if (slug === 'bitrix24') {
    const refreshed = await api.getBitrix24Pipelines(userId)
    return refreshed?.warning === 'stages_stale' ? 'Показаны ранее сохранённые стадии.' : null
  }
  if (slug === 'amocrm') {
    try {
      const synced = await api.syncPipelines(userId)
      if (!hasPipelineStages(synced)) throw new Error('amocrm_stages_empty')
      return null
    } catch (_) {
      const cached = await api.getPipelines(userId).catch(() => ({ pipelines: [] }))
      if (!hasPipelineStages(cached)) {
        throw new Error('Не удалось загрузить стадии amoCRM. Проверьте подключение и повторите.')
      }
      return 'Показаны ранее сохранённые стадии.'
    }
  }
  return null
}

// Справочники значений фильтра подключения: сотрудники и первая страница
// контрагентов каждого типа с подписями сохранённых значений — их может не
// оказаться на первой странице. `optional` — отказ не роняет экран, а
// называется в списке незагруженного.
export async function loadFilterDirectories (slug, connectionId, filters, liveFields, optional) {
  let employees = []
  let counterparties = { company: [], contact: [] }
  const counterpartyDirectory = COUNTERPARTY_DIRECTORIES[slug]
  if (counterpartyDirectory) {
    const saved = savedCounterpartyValues(filters, liveFields)
    const directory = await optional(
      Promise.all([
        counterpartyDirectory.list(connectionId, { type: 'company', tab: 'all' }),
        counterpartyDirectory.list(connectionId, { type: 'contact', tab: 'all' }),
        ...saved.map(({ type, value }) =>
          counterpartyDirectory.list(connectionId, { type, tab: 'all', q: value }))
      ]),
      'Справочник контрагентов',
      [{ mappings: [] }, { mappings: [] }]
    )
    const [companies, contacts, ...savedPages] = directory
    const savedOptions = type => savedPages
      .flatMap(page => counterpartyOptionsOf(page, type, counterpartyDirectory))
      .filter(option => saved.some(item => item.type === type && item.value === option.value))
    counterparties = {
      company: uniqueOptions([...savedOptions('company'), ...counterpartyOptionsOf(companies, 'company', counterpartyDirectory)]),
      contact: uniqueOptions([...savedOptions('contact'), ...counterpartyOptionsOf(contacts, 'contact', counterpartyDirectory)])
    }
  }
  if (slug === 'bitrix24') {
    const employeeDirectory = await optional(
      api.getBitrix24EmployeeMappings(connectionId),
      'Справочник сотрудников',
      { mappings: [] }
    )
    employees = (employeeDirectory.mappings || [])
      .filter(employee => employee.is_stale !== true)
      .map(employee => ({
        value: String(employee.b24_user_id),
        label: employee.b24_name || String(employee.b24_user_id)
      }))
  } else if (slug === 'amocrm') {
    const employeeDirectory = await optional(
      api.getReferenceEditor(connectionId, 'employees'),
      'Справочник сотрудников',
      { items: [] }
    )
    employees = (employeeDirectory.items || [])
      .filter(employee => employee.is_stale !== true)
      .map(employee => ({
        value: String(employee.source_id),
        label: employee.source_name || String(employee.source_id)
      }))
  }
  return { employees, counterparties }
}

// Значения условий фильтра: справочники подключения, значения из портала по
// выбору поля и поиск контрагента по справочнику.
export function useFilterSources (slug) {
  // Справочники, которых нет ни в поле, ни в уже загруженных списках: портал
  // спрашивается в момент выбора поля. Ключ — поле, значение — состояние
  // загрузки, чтобы отказ было видно на самом поле, а не в общем экране ошибки.
  const [providerOptions, setProviderOptions] = useState({})
  const [sourceEmployees, setSourceEmployees] = useState([])
  // Справочник контрагентов подключения: значение фильтра по компании или
  // контакту выбирается из него, а не набирается руками. У большого портала
  // справочник целиком не грузится (counterparty-large-accounts): здесь
  // первая страница и результаты поиска по введённому тексту.
  const [sourceCounterparties, setSourceCounterparties] = useState({ company: [], contact: [] })
  // Подписи всех встреченных записей справочника: выбранное значение остаётся
  // подписанным, даже когда новый поиск его не вернул.
  const counterpartyLabels = useRef(new Map())
  const counterpartySearch = useRef({ connectionId: null, timers: {}, requests: {} })
  // Загружается один раз на поле: повторный выбор того же поля в другой строке
  // фильтра не идёт в портал заново.
  // Уже запрошенные поля держим в ref, а не в state: решение «идти ли в
  // портал» должно приниматься сразу, а обновление состояния асинхронно —
  // на этом первая версия и не работала, запрос не уходил вовсе.
  const requestedOptions = useRef(new Set())

  const reset = useCallback(() => {
    setSourceEmployees([])
    setSourceCounterparties({ company: [], contact: [] })
    counterpartyLabels.current = new Map()
    counterpartySearch.current.connectionId = null
  }, [])

  const apply = useCallback((connectionId, directories) => {
    const counterparties = directories?.counterparties || { company: [], contact: [] }
    counterpartySearch.current.connectionId = connectionId && COUNTERPARTY_DIRECTORIES[slug] ? connectionId : null
    for (const type of ['company', 'contact']) {
      for (const option of counterparties[type]) counterpartyLabels.current.set(`${type}:${option.value}`, option.label)
    }
    setSourceEmployees(directories?.employees || [])
    setSourceCounterparties(counterparties)
  }, [slug])

  const loadProviderOptions = useCallback(async (fieldKey, { force = false } = {}) => {
    if (!fieldKey) return
    if (!force && requestedOptions.current.has(fieldKey)) return
    requestedOptions.current.add(fieldKey)
    setProviderOptions(current => ({ ...current, [fieldKey]: { status: 'loading', options: [] } }))
    try {
      const result = await api.getFieldOptions(slug, fieldKey)
      setProviderOptions(current => ({
        ...current,
        [fieldKey]: { status: 'ready', options: result?.options || [] }
      }))
    } catch (err) {
      // Свободный ввод здесь был бы худшим из решений: он возвращает дефект,
      // ради которого список и заводился, причём молча. Отметку о запросе
      // снимаем — иначе «Повторить» било бы в пустоту.
      requestedOptions.current.delete(fieldKey)
      setProviderOptions(current => ({
        ...current,
        [fieldKey]: { status: 'error', options: [], error: err.message }
      }))
    }
  }, [slug])

  // Поиск значения фильтра по справочнику контрагентов на сервере. Пауза
  // ввода — чтобы не спрашивать сервер на каждую букву; ответ на устаревший
  // ввод не применяется.
  function searchCounterparties (type, text) {
    const state = counterpartySearch.current
    const directory = COUNTERPARTY_DIRECTORIES[slug]
    if (!state.connectionId || !directory) return
    clearTimeout(state.timers[type])
    state.timers[type] = setTimeout(async () => {
      const request = (state.requests[type] || 0) + 1
      state.requests[type] = request
      const q = text.trim()
      try {
        const data = await directory.list(
          state.connectionId,
          q ? { type, tab: 'all', q } : { type, tab: 'all' }
        )
        if (state.requests[type] !== request) return
        const options = counterpartyOptionsOf(data, type, directory)
        for (const option of options) counterpartyLabels.current.set(`${type}:${option.value}`, option.label)
        setSourceCounterparties(current => ({ ...current, [type]: options }))
      } catch (_) {
        // Отказ поиска оставляет прежний список: выбранное значение не теряется.
      }
    }, COUNTERPARTY_SEARCH_DELAY_MS)
  }

  function counterpartyChoices (type, selected) {
    const options = sourceCounterparties[type]
    if (!selected || options.some(option => option.value === selected)) return options
    const label = counterpartyLabels.current.get(`${type}:${selected}`)
    return label ? [{ value: selected, label }, ...options] : options
  }

  return {
    apply,
    counterpartyChoices,
    loadProviderOptions,
    providerOptions,
    reset,
    searchCounterparties,
    sourceEmployees
  }
}

// Условие может приехать уже настроенным — из сохранённого сценария или
// выгрузки. Тогда значения из портала нужны сразу, иначе поле откроется
// пустым списком.
export function useSavedProviderOptions (filters, liveFields, loadProviderOptions) {
  useEffect(() => {
    const keys = (filters?.groups || [])
      .flat()
      .map(condition => liveFields.find(item => item.key === condition.field))
      .filter(field => field?.options_source === 'provider')
      .map(field => field.key)
    for (const key of new Set(keys)) loadProviderOptions(key)
  }, [filters, liveFields, loadProviderOptions])
}
